/* eslint-disable no-unused-vars */
import React, { useState, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller } from 'react-hook-form';
import moment from 'moment';

import 'react-datepicker/dist/react-datepicker.css';
import Label from '@components/label';
import DatePickerIcon from './date-picker-icon';
import CustomHeader from './render-cusom-header';
import './date.css';

import style from './date-picker.module.scss';

const DatePicker = ({
  readOnly,
  name,
  control,
  className,
  id,
  defaultVal,
  isDisabled,
  handleClick,
  maxDate,
  minDate,
  placeholder,
  reconciled,
  label,
  error,
  dot,
  isClearable,
  customFunc,
  isRequired = false,
  isRequiredMsg = '',
  timePicker = false,
  showReconcileFeatherIconWhenValueChanged = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isDatePickerOpen, setDatePickerOpen] = useState(false);
  const rawInputRef = useRef(null);
  const isTypingRef = useRef(false);

  const handleInputClick = (value) => {
    setDatePickerOpen(value);
  };

  const parseTimeInput = (raw) => {
    if (!raw || !raw.trim()) return null;
    const trimmed = raw.trim();
    // 24-hour format: "23", "07:36", "14:30"
    const match24 = trimmed.match(/^(\d{1,2})(?::(\d{2}))?$/);
    if (match24) {
      const hours = parseInt(match24[1], 10);
      const minutes = parseInt(match24[2] || '0', 10);
      if (hours >= 0 && hours <= 23 && minutes >= 0 && minutes <= 59) {
        const d = new Date();
        d.setHours(hours, minutes, 0, 0);
        return d;
      }
    }
    // 12-hour format: "10:30 PM", "11 AM"
    const match12 = trimmed.match(/^(\d{1,2})(?::(\d{2}))?\s*(am|pm)$/i);
    if (match12) {
      let hours = parseInt(match12[1], 10);
      const minutes = parseInt(match12[2] || '0', 10);
      const period = match12[3].toLowerCase();
      if (hours >= 1 && hours <= 12 && minutes >= 0 && minutes <= 59) {
        if (period === 'am' && hours === 12) hours = 0;
        if (period === 'pm' && hours !== 12) hours += 12;
        const d = new Date();
        d.setHours(hours, minutes, 0, 0);
        return d;
      }
    }
    return null;
  };

  return (
    <>
      <div className={`${style.main} ${className}`}>
        {label && (
          <Label
            reconciled={
              showReconcileFeatherIconWhenValueChanged
                ? reconciled
                  ? reconciled
                  : false
                : reconciled
            }
            label={label}
            dot={dot}
            error={error}
            inLineStyle={{ paddingLeft: '8px' }}
          />
        )}
        <div onClick={handleClick} style={{ position: 'relative' }}>
          <Controller
            rules={{
              required: isRequired ? isRequiredMsg : ''
            }}
            name={name}
            control={control}
            defaultValue={defaultVal || null}
            render={({ field: { onChange, value } }) => {
              return (
                <>
                  <ReactDatePicker
                    onKeyDown={(e) => {
                      if (e.key === 'Tab') {
                        setDatePickerOpen(false);
                        setIsOpen(false);
                      }
                      if (timePicker && e.key === 'Enter') {
                        e.preventDefault();
                        const parsed = parseTimeInput(rawInputRef.current);
                        if (parsed) {
                          rawInputRef.current = null;
                          isTypingRef.current = false;
                          onChange(parsed);
                          setIsOpen(false);
                          handleInputClick(false);
                          customFunc && customFunc(parsed);
                        }
                      }
                    }}
                    onFocus={() => setIsOpen(true)}
                    onBlur={() => {
                      setIsOpen(false);
                      if (timePicker && isTypingRef.current) {
                        const parsed = parseTimeInput(rawInputRef.current);
                        if (parsed) {
                          rawInputRef.current = null;
                          isTypingRef.current = false;
                          onChange(parsed);
                          handleInputClick(false);
                          customFunc && customFunc(parsed);
                        }
                        isTypingRef.current = false;
                      }
                    }}
                    autoComplete="off"
                    isClearable={isClearable}
                    shouldRenderSuggestions={false}
                    previousMonthAriaLabel={style.classRed}
                    shouldCloseOnSelect
                    // readOnly={timePicker ? false : readOnly}
                    onInputClick={() => {
                      handleInputClick(!isDatePickerOpen);
                    }}
                    onClickOutside={() => {
                      handleInputClick(!isDatePickerOpen);
                    }}
                    open={isDatePickerOpen}
                    onChangeRaw={(e) => {
                      rawInputRef.current = e?.target?.value || null;
                      if (timePicker) {
                        isTypingRef.current = true;
                      }
                    }}
                    onChange={(date) => {
                      if (!date) {
                        onChange(null);
                        return;
                      }
                      if (timePicker) {
                        if (isTypingRef.current) {
                          // Update value so dropdown scrolls, but don't close
                          onChange(date);
                          return;
                        }
                        // Dropdown selection — use date directly and close
                        isTypingRef.current = false;
                        rawInputRef.current = null;
                        onChange(date);
                        setIsOpen(false);
                        handleInputClick(false);
                        customFunc && customFunc(date);
                      } else {
                        let formatted;
                        const raw = rawInputRef.current;
                        if (raw && /^\d{4}-\d{1,2}-\d{1,2}$/.test(raw.trim())) {
                          const parsed = moment(raw.trim(), 'YYYY-MM-DD');
                          formatted = parsed.isValid()
                            ? parsed.format('YYYY-MM-DD')
                            : moment(date).format('YYYY-MM-DD');
                        } else {
                          formatted = moment(date).format('YYYY-MM-DD');
                        }
                        rawInputRef.current = null;
                        onChange(formatted);
                        setIsOpen(false);
                        handleInputClick(!isDatePickerOpen);
                        customFunc && customFunc(formatted);
                      }
                    }}
                    className={error ? style.borderClass : style.inpDiv}
                    placeholderText={placeholder ? placeholder : ''}
                    disabled={isDisabled}
                    showTimeSelect={timePicker}
                    showTimeSelectOnly={timePicker}
                    timeIntervals={timePicker ? '1' : '0'}
                    timeCaption={timePicker ? 'Time' : ''}
                    selected={
                      value == 'Invalid Date' || (moment.isMoment(value) && !value.isValid())
                        ? null
                        : value
                        ? timePicker
                          ? new Date(moment(value))
                          : moment(value, 'YYYY-MM-DD').toDate()
                        : null || null
                    }
                    maxDate={maxDate && maxDate}
                    minDate={minDate && minDate}
                    dateFormat={timePicker ? 'hh:mm aa' : 'MMM. d, yyyy'}
                    id={id}
                    renderCustomHeader={(props) => (
                      <CustomHeader
                        {...{
                          ...props
                        }}
                      />
                    )}
                  />
                </>
              );
            }}
          />

          <DatePickerIcon
            {...{
              id,
              isOpen,
              timePicker
            }}
          />
        </div>
      </div>
    </>
  );
};

export default DatePicker;
